import {useState, useEffect, useMemo} from 'react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {usePreview} from '../contexts/StorefrontPreviewContext'

const CACHE_TTL_MS = 60 * 60 * 1000 // 1 hour

// Module-level in-memory cache shared across all hook instances
const _responseCache = new Map()

// In-flight promise map — prevents duplicate concurrent fetches for the same key
const _pendingFetches = new Map()

// ---------------------------------------------------------------------------
// Prefetch queue — limits background prefetches to MAX_CONCURRENT so they
// don't saturate the browser connection pool and starve page-content fetches
// (hero, footer, carousels, etc.) which always bypass this queue.
// ---------------------------------------------------------------------------
const MAX_CONCURRENT_PREFETCHES = 2
let _prefetchActive = 0
const _prefetchQueue = []

const _processPrefetchQueue = () => {
    while (_prefetchActive < MAX_CONCURRENT_PREFETCHES && _prefetchQueue.length > 0) {
        const {slotParam, previewData, resolve, reject} = _prefetchQueue.shift()
        _prefetchActive++
        const cacheKey = _buildContentCacheKey(slotParam, previewData)
        _fetchAndCache(cacheKey, slotParam, previewData, undefined)
            .then(resolve)
            .catch(reject)
            .finally(() => {
                _prefetchActive--
                _processPrefetchQueue()
            })
    }
}

const _queuePrefetch = (slotParam, previewData) =>
    new Promise((resolve, reject) => {
        _prefetchQueue.push({slotParam, previewData, resolve, reject})
        _processPrefetchQueue()
    })

// ---------------------------------------------------------------------------
// appConfig is static — resolving it once at module level removes it from
// useEffect dependency arrays, which was causing effects to re-run on every
// render (each getConfig() call returns a new object reference).
// ---------------------------------------------------------------------------
let _appConfig = null
const _getAppConfig = () => {
    if (!_appConfig) {
        try {
            _appConfig = getConfig().app
        } catch {
            // getConfig may throw during SSR before config is ready
        }
    }
    return _appConfig
}

const _getFromCache = (key) => {
    const entry = _responseCache.get(key)
    if (!entry) return null
    if (Date.now() - entry.ts > CACHE_TTL_MS) {
        _responseCache.delete(key)
        return null
    }
    return entry.value
}

const _setCache = (key, value) => {
    _responseCache.set(key, {value, ts: Date.now()})
}

/**
 * Single source of truth for cache / dedupe keys: slot query string, or
 * slot|serialized preview when MRT preview params are present.
 */
const _buildContentCacheKey = (slotParam, previewData = {}) => {
    const previewKey =
        previewData && Object.keys(previewData).length > 0 ? JSON.stringify(previewData) : ''
    return previewKey ? `${slotParam}|${previewKey}` : slotParam
}

/**
 * Local PWA dev (localhost) needs the site-scoped OCAPI path so requests hit the
 * proxy the same way as other local OCAPI calls. Managed Runtime and production
 * storefront URLs keep their existing path shapes — do not change those.
 */
const _isLocalDevHostname = () => {
    if (typeof window === 'undefined') return false
    const h = window.location.hostname
    return h === 'localhost' || h === '127.0.0.1' || h === '[::1]'
}

const _buildFetchArgs = (slotParam, previewData) => {
    const appConfig = _getAppConfig()
    let fetchOptions = {}
    if (appConfig?.storefrontApi?.userName && appConfig?.storefrontApi?.password) {
        fetchOptions = {
            headers: {
                Authorization:
                    'Basic ' +
                    btoa(`${appConfig.storefrontApi.userName}:${appConfig.storefrontApi.password}`)
            }
        }
    }

    const proxyPathOcapi = appConfig?.commerceAPI?.proxyPathOcapi || ''
    const siteId = appConfig?.commerceAPI?.parameters?.siteId || ''
    const href = typeof window !== 'undefined' ? window.location.href || '' : ''
    const isMobifyHost = href.includes('mobify-storefront.com')
    const isLocalDev = _isLocalDevHostname()

    let previewQueryString = ''
    let isPreview = false
    if (isMobifyHost) {
        previewQueryString = Object.keys(previewData).length
            ? '&' + new URLSearchParams(previewData).toString()
            : ''
        isPreview = true
    }

    let url
    if (isLocalDev && proxyPathOcapi && siteId) {
        // Matches local dev parallel file: site in path so the dev proxy routes correctly.
        url = `${proxyPathOcapi}/s/${siteId}/getcustomcontent/?${slotParam}${previewQueryString}`
    } else if (isMobifyHost && proxyPathOcapi) {
        // Dev / stage / PR MRT — unchanged from production behavior.
        url = `${proxyPathOcapi}/getcustomcontent/?${slotParam}${previewQueryString}`
    } else {
        // Production storefront (and SSR / no window): same-origin relative path.
        url = `/getcustomcontent/?${slotParam}${previewQueryString}`
    }

    return {
        url,
        fetchOptions,
        isPreview
    }
}

/**
 * Shared fetch with in-flight deduplication.
 * cacheKey — dedupe + _setCache (includes preview when applicable).
 * slotParam — URL query segment only (never includes preview).
 * Concurrent callers for the same cacheKey share a single in-flight promise.
 */
const _fetchAndCache = (cacheKey, slotParam, previewData, signal) => {
    if (_pendingFetches.has(cacheKey)) {
        return _pendingFetches.get(cacheKey)
    }

    const promise = (async () => {
        const {url, fetchOptions, isPreview} = _buildFetchArgs(slotParam, previewData)
        const res = await fetch(url, {...fetchOptions, signal})
        if (!res.ok) throw new Error('Failed to fetch content')
        let html = await res.text()
        if (isPreview) {
            html = html.replace(/href=(["'])https:\/\/(dev|staging)\.cavenders\.com/g, 'href=$1')
        }
        _setCache(cacheKey, html)
        return html
    })().finally(() => {
        _pendingFetches.delete(cacheKey)
    })

    _pendingFetches.set(cacheKey, promise)
    return promise
}

/**
 * Standalone prefetch — warms the cache for a slot before the user hovers.
 * Skips if already cached or in-flight. Goes through the throttled queue so
 * background prefetches do not compete with page-content fetches.
 */
export const prefetchSlotContent = async (slotParam, _ignoredAppConfig, previewData = {}) => {
    const cacheKey = _buildContentCacheKey(slotParam, previewData)
    if (_getFromCache(cacheKey) !== null) return
    if (_pendingFetches.has(cacheKey)) return

    try {
        await _queuePrefetch(slotParam, previewData)
    } catch (err) {
        console.error('Error prefetching slot content:', err)
    }
}

const useCustomFetch = (token, ids, isSlot = true) => {
    const slotParams = useMemo(
        () => (ids ? (isSlot ? `c_contentSlotIDs=${ids}` : `c_contentAssetIDs=${ids}`) : null),
        [ids, isSlot]
    )

    const {previewData = {}} = usePreview()

    const cacheKey = useMemo(() => {
        if (!slotParams) return null
        return _buildContentCacheKey(slotParams, previewData)
    }, [slotParams, previewData])

    const initialCached = cacheKey ? _getFromCache(cacheKey) : null

    const [response, setResponse] = useState(() => initialCached)
    const [loading, setLoading] = useState(() => !ids || initialCached === null)
    const [error, setError] = useState(null)

    useEffect(() => {
        if (!cacheKey) return

        const cached = _getFromCache(cacheKey)
        if (cached !== null) {
            setResponse(cached)
            setLoading(false)
            return
        }

        const controller = new AbortController()

        const fetchData = async () => {
            setLoading(true)
            setError(null)

            try {
                // Page-content fetches bypass the prefetch queue — they are
                // immediate and take priority over background prefetches.
                const html = await _fetchAndCache(
                    cacheKey,
                    slotParams,
                    previewData,
                    controller.signal
                )
                if (!controller.signal.aborted) {
                    setResponse(html)
                }
            } catch (err) {
                if (err.name === 'AbortError') return
                console.error('Error in fetching custom API:', err)
                setError(err.message || 'An unexpected error occurred')
            } finally {
                if (!controller.signal.aborted) {
                    setLoading(false)
                }
            }
        }

        fetchData()

        return () => {
            controller.abort()
        }
    }, [cacheKey])
    // Only cacheKey: it already reflects slot + preview. slotParams/previewData
    // must not be deps — new object refs would retrigger without a key change.

    return {response, loading, error}
}

export default useCustomFetch
